import { QueryObserverResult, useQuery } from '@tanstack/react-query'
import { constructFormAddressString } from 'app/views/forms/common/Common'
import { checkFormValidityBase } from 'app/views/forms/formHelpersValidation'
import { GRANTS_PATH } from 'app/views/grants/GrantsConstants'
import { RootStateOrAny, useSelector } from 'react-redux'
import { useForm } from './useForm'

interface UseOrganizationValidityBaseProps {
  accountId: string
  userId?: string
  formId?: string
}

interface UseOrganizationValidityBaseResult {
  isValid: boolean
  isLoadedAndInvalid: boolean
  error: unknown
  isLoading: boolean
  refetch: () => Promise<QueryObserverResult<boolean, unknown>>
}

/**
 * Base hook for checking organization validity
 */
export const useOrganizationValidityBase = (props: UseOrganizationValidityBaseProps): UseOrganizationValidityBaseResult => {
  const {
    accountId,
    userId,
    formId
  } = props

  const { data: form, isLoading: isLoadingForm, error: formError } = useForm({ formId })

  const organization = useSelector((state: RootStateOrAny) => state.organization)

  const { data: validityResult, isLoading: isLoadingValidity, error: validityError, refetch } = useQuery({
    queryKey: ['form-validity', formId, accountId, userId],
    queryFn: () => {
      if (formId === undefined) {
        throw new Error('formId is required')
      }
      const isValid = checkFormValidityBase({
        form,
        targetFormUrl: constructFormAddressString({
          ids: {
            Account: accountId,
            User: userId
          },
          objectsConnected: form?.objectsConnected
        }),
        pathname: GRANTS_PATH.ORGANIZATION_DETAILS
      })

      return isValid
    },
    enabled: Boolean(form) && Boolean(organization?.id),
    refetchInterval: 1000 * 60 * 2,
    staleTime: 1000 * 60 * 2
  })

  const isValid = form !== undefined && Boolean(validityResult)

  console.log('useOrganizationValidityBase', { validityError, formError, isValid, isLoadingForm, isLoadingValidity })

  const isLoadedAndInvalid = Boolean(validityError) || Boolean(formError) || (!isValid && (form !== undefined ? true : !isLoadingForm) && !isLoadingValidity)

  return {
    isValid,
    isLoadedAndInvalid,
    error: validityError ?? formError,
    isLoading: isLoadingForm || isLoadingValidity,
    refetch
  }
}
